/**
 * Pool member & Overwatch APIs (Phase 1 co-ownership).
 *
 * All endpoints live behind the REST API Gateway (Cognito JWT). Response
 * shapes mirror ``rest_handlers.py``:
 *
 *   GET    /networks/{id}/pools/{poolId}/members      → { members, count, ... }
 *   POST   /networks/{id}/pools/{poolId}/members      → { member, wasExisting, authzPath }
 *   DELETE /networks/{id}/pools/{poolId}/members/{sub}→ { removedSub, poolId }
 *   POST   /networks/{id}/pools/{poolId}/members/self → { member, alreadyCreator }
 *   GET    /tenants/{tenantId}/pools                  → { pools, count }
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function listPoolMembers(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/members`,
  );
}

/**
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ email?: string, sub?: string, role?: 'CREATOR'|'SUPERVISOR' }} body
 */
export async function addPoolMember(networkId, poolId, body) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/members`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
}

export async function removePoolMember(networkId, poolId, sub) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/members/${encodePath(sub)}`,
    { method: 'DELETE' },
  );
}

/**
 * Overwatch self-join: workspace OWNER/ADMIN grants themselves CREATOR on any
 * pool in the tenant. The audit trail (`MEMBERSHIP_SELF_ELEVATED`) records
 * who rescued which pool and why.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} [reason]  free-form note for the audit row
 */
export async function selfElevatePoolMember(networkId, poolId, reason) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/members/self`,
    {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
}

/**
 * Tenant-wide pool list (workspace OWNER/ADMIN only). Each pool has
 * ``isOrphaned`` + ``creatorCount`` so the discovery UI can surface rescues.
 *
 * @param {string} tenantId
 * @param {{ limit?: number }} [opts]
 */
export async function listTenantPools(tenantId, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  const q = qs.toString();
  return authedFetch(
    `/tenants/${encodePath(tenantId)}/pools${q ? `?${q}` : ''}`,
  );
}

/**
 * Phase B — pool runtime governance. Currently only flips ``triggerMode``
 * (``HEADLESS`` / ``HYBRID`` / ``MANUAL``) but the body shape is forward-
 * compatible with future per-pool settings (rate caps, alert routing, …)
 * so adding a knob never invalidates older clients.
 *
 * Server-side authorization mirrors the member-management endpoints:
 * caller must be the pool ``CREATOR`` or a workspace ``OWNER`` / ``ADMIN``.
 *
 * Response shape::
 *
 *     {
 *       pool: NetworkPool,                 // post-update row (full)
 *       settings: { triggerMode: string },
 *       previousSettings?: { triggerMode },
 *       changed: boolean,                  // false if no diff (idempotent)
 *       authzPath: 'CREATOR' | 'OVERWATCH'
 *     }
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ trigger: 'HEADLESS'|'HYBRID'|'MANUAL' }} body
 */
export async function updatePoolSettings(networkId, poolId, body) {
  if (!body || typeof body !== 'object' || (!body.trigger && !body.builderExecutionMode)) {
    throw new Error('updatePoolSettings: body.trigger or body.builderExecutionMode is required');
  }
  const payload = {};
  if (body.trigger) payload.trigger = String(body.trigger).trim().toUpperCase();
  if (body.builderExecutionMode) payload.builderExecutionMode = String(body.builderExecutionMode).trim();
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/settings`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Phase C — per-pool, per-agent supervisor overrides.
 *
 * Lists every agent in the network with its **effective** supervisor
 * email plus the source flag (``OVERRIDE`` | ``WORKBENCH`` | ``UNSET``).
 * The drawer renders this directly without merging defaults +
 * overrides client-side.
 *
 * Server response::
 *
 *     {
 *       networkId, poolId, releaseVersion,
 *       agents: [{
 *         agentId,
 *         effectiveSupervisorEmail,
 *         source: 'OVERRIDE' | 'WORKBENCH' | 'UNSET',
 *         workbenchDefaultEmail,
 *         override?: { supervisorEmail, setBy, setAt, ... },
 *         effectiveSupervisor?: { sub, email, name },
 *       }],
 *       count,
 *     }
 *
 * @param {string} networkId
 * @param {string} poolId
 */
export async function listPoolAgentSupervisors(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agent-supervisors`,
  );
}

/**
 * Set the per-pool, per-agent supervisor inbox list override.
 *
 * Authorization: pool ``CREATOR`` or workspace ``OWNER`` / ``ADMIN``.
 * Each address **must** belong to a workspace member of the same
 * tenant; the server refuses external addresses (drafts contain PII).
 *
 * Idempotent — re-applying the same ordered list returns ``changed: false``.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} agentId
 * @param {{ emails: string[], email?: string, reason?: string }} body
 */
export async function setPoolAgentSupervisor(networkId, poolId, agentId, body) {
  if (!body || typeof body !== 'object') {
    throw new Error('setPoolAgentSupervisor: body is required');
  }
  let emails = Array.isArray(body.emails) ? body.emails : null;
  if (!emails || emails.length === 0) {
    const legacy = String(body.email || '').trim().toLowerCase();
    emails = legacy ? [legacy] : [];
  }
  const norm = [...new Set(emails.map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))];
  if (!norm.length) {
    throw new Error('setPoolAgentSupervisor: body.emails must be a non-empty array (or legacy body.email)');
  }
  const payload = { emails: norm };
  if (body.reason) {
    payload.reason = String(body.reason).trim();
  }
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agents/${encodePath(agentId)}/supervisor`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Clear the per-pool override; effective supervisor falls back to the
 * workbench default for that agent.
 *
 * Idempotent — clearing a non-existent override returns
 * ``changed: false``.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} agentId
 */
export async function clearPoolAgentSupervisor(networkId, poolId, agentId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agents/${encodePath(agentId)}/supervisor`,
    { method: 'DELETE' },
  );
}

/**
 * List the pool's connector overrides — the per-pool pin map keyed by
 * tool name. Pool-member readable; mutations are POOL CREATOR only.
 *
 * Connector context hierarchy: Tenant ⊃ Network ⊃ Pool. The pool sees
 * its own overrides plus inherits the network defaults baked into the
 * release snapshot. See ``.cursor/rules/connector-context-hierarchy.mdc``.
 *
 * @returns {Promise<{networkId, poolId, releaseVersion, overrides:[], overridesByTool:{}, count:number}>}
 */
export async function listPoolConnectorOverrides(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/connector-overrides`,
  );
}

// ---------------------------------------------------------------------------
// Room-context injection override (per-pool) — the room owner's tri-state
// switch (Inherit / On / Off) that beats the network's snapshotted room-context
// setting. Inherit = no override row (DELETE). See CONTEXT.md §13a.
// ---------------------------------------------------------------------------

/** GET → ``{mode: 'on'|'off'|null}`` where null = inherit the network setting. */
export async function getRoomContextOverride(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/room-context-override`,
  );
}

/** PUT an explicit override. ``mode`` ∈ ``'on' | 'off'`` (use clear for inherit). */
export async function setRoomContextOverride(networkId, poolId, mode) {
  const m = String(mode || '').trim().toLowerCase();
  if (m !== 'on' && m !== 'off') {
    throw new Error("setRoomContextOverride: mode must be 'on' or 'off'");
  }
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/room-context-override`,
    { method: 'PUT', body: JSON.stringify({ mode: m }) },
  );
}

/** DELETE the override → the pool reverts to inheriting the network setting. */
export async function clearRoomContextOverride(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/room-context-override`,
    { method: 'DELETE' },
  );
}

/**
 * Set the objective-JUDGE model for this room (SCHEDULER.md §11.1b). Shares the
 * room-context-override route (PUT) — judge config is independent of the
 * room-context toggle. ``model`` ∈ fast|think|reason|haiku|sonnet|opus; pass ''
 * to clear (judge reverts to its default tier). The GET above returns
 * ``judgeModel`` alongside ``mode``.
 */
export async function setPoolJudgeModel(networkId, poolId, model) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/room-context-override`,
    { method: 'PUT', body: JSON.stringify({ judgeModel: String(model || '').trim() }) },
  );
}

/**
 * Pin a registry secret as the pool's default for ``toolName``.
 *
 * Authorization: pool CREATOR or workspace OWNER/ADMIN.
 *
 * Visibility: ``secretId`` MUST resolve to a row in the pool's
 * inheritance chain (TENANT ∪ NETWORK ∪ this POOL). The server rejects
 * sibling-pool ids with ``reason: scope_pool_mismatch`` and cross-tool
 * ids with ``reason: tool_mismatch`` so the UI can render targeted
 * messages instead of a generic 400.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} toolName  e.g. ``'code-connector'``
 * @param {{secretId:string, reason?:string}} body
 */
export async function setPoolConnectorOverride(networkId, poolId, toolName, body) {
  if (!body || typeof body !== 'object' || !body.secretId) {
    throw new Error('setPoolConnectorOverride: body.secretId is required');
  }
  const payload = { secretId: String(body.secretId).trim() };
  if (body.reason) payload.reason = String(body.reason).trim();
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/connector-overrides/${encodePath(toolName)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Clear the pool's override for ``toolName``. After this call the pool
 * falls back to the network default (and then to no connector / no
 * credentials at runtime).
 *
 * Idempotent — clearing a non-existent override returns 200 with
 * ``changed: false``.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} toolName
 */
export async function clearPoolConnectorOverride(networkId, poolId, toolName) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/connector-overrides/${encodePath(toolName)}`,
    { method: 'DELETE' },
  );
}

/**
 * Matrix of agents in the released network × registry connector tools each
 * agent carries, with effective secret id and resolution source
 * (OVERRIDE | LEGACY_POOL | NETWORK_DEFAULT | NONE).
 *
 * @returns {Promise<{networkId, poolId, releaseVersion, agents:Array}>}
 */
export async function listAgentConnectorOverrides(networkId, poolId) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agent-connector-overrides`,
  );
}

/**
 * Pin a registry secret for a specific agent × tool in this pool.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} agentId
 * @param {string} toolName
 * @param {{secretId:string, reason?:string}} body
 */
export async function setAgentPoolConnectorOverride(
  networkId,
  poolId,
  agentId,
  toolName,
  body,
) {
  if (!body || typeof body !== 'object' || !body.secretId) {
    throw new Error('setAgentPoolConnectorOverride: body.secretId is required');
  }
  const payload = { secretId: String(body.secretId).trim() };
  if (body.reason) payload.reason = String(body.reason).trim();
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agents/${encodePath(agentId)}/connector-overrides/${encodePath(toolName)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
}

/**
 * Clear per-agent pool override; effective connector falls back to pool-wide
 * then network default (same resolution order as runtime).
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} agentId
 * @param {string} toolName
 */
export async function clearAgentPoolConnectorOverride(
  networkId,
  poolId,
  agentId,
  toolName,
) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/agents/${encodePath(agentId)}/connector-overrides/${encodePath(toolName)}`,
    { method: 'DELETE' },
  );
}

/**
 * Section 4 of the version+snapshot rework — flip a single room's
 * snapshot pin. Pass ``snapshotRef = null`` (or empty string) to clear
 * the pin so the room reverts to its parent Version's release-baseline.
 *
 * The room's SFN alias is **unaffected**. Snapshots scope to behavior
 * overlay, not the underlying ASL — switching the pin is a one-write
 * DDB operation, no AWS calls.
 *
 * Response shape:
 *   { pool: NetworkPool, snapshotRef: string|null }
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string|null} snapshotRef  opaque key from snapshotDomKey()
 */
export async function setPoolSnapshot(networkId, poolId, snapshotRef) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/snapshot`,
    {
      method: 'POST',
      body: JSON.stringify({
        snapshotRef: snapshotRef ? String(snapshotRef).trim() : null,
      }),
    },
  );
}

// ---------------------------------------------------------------------------
// MCP credentials (Phase 2). Mounted under the /tools proxy (zero extra API-GW
// resources) — paths encode the room scope as segments.
// ---------------------------------------------------------------------------

/**
 * Per-MCP configured status for a room. Read-only; never returns secrets.
 * @returns {Promise<{ mcps: Array<{id,authRequired,configured,scope}> }>}
 */
export async function getMcpCredentialStatus(networkId, poolId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}`,
  );
}

/**
 * Set (room-scope) credentials for one authenticated MCP. Plaintext goes to
 * SSM via the broker; only the pointer is stored. Room OWNER/BUILDER only.
 * @param {Record<string,string>} credentials  keys per the MCP's CredentialSchema
 */
export async function setMcpCredential(networkId, poolId, mcpId, credentials) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/${encodePath(mcpId)}`,
    { method: 'PUT', body: JSON.stringify({ credentials: credentials || {} }) },
  );
}

/** Clear a room's credential for one MCP. Room OWNER/BUILDER only. */
export async function clearMcpCredential(networkId, poolId, mcpId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/${encodePath(mcpId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Set credentials for one authenticated MCP scoped to a SPECIFIC agent
 * (overrides the room default; resolved AGENT ⊃ POOL ⊃ NETWORK ⊃ TENANT).
 * Lets two builders in the same room hold distinct identities.
 * Room OWNER/BUILDER only.
 */
export async function setAgentMcpCredential(networkId, poolId, agentId, mcpId, credentials) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agents/${encodePath(agentId)}/${encodePath(mcpId)}`,
    { method: 'PUT', body: JSON.stringify({ credentials: credentials || {} }) },
  );
}

/** Clear one agent's per-agent credential for an MCP (falls back to room
 *  default afterwards). Room OWNER/BUILDER only. */
export async function clearAgentMcpCredential(networkId, poolId, agentId, mcpId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agents/${encodePath(agentId)}/${encodePath(mcpId)}`,
    { method: 'DELETE' },
  );
}

// ── Per-agent MCP INSTANCE overrides (room-drawer "Tools" pin) ──────────────
// The MCP analog of the connector per-agent override: pins WHICH configured
// instance (created in the Connections catalog) an agent uses for an MCP. A
// POINTER to an instanceId, not the credential. Room OWNER/BUILDER only.

/** List per-agent MCP-instance overrides for a room. → { overrides: [{agentId, mcpId, instanceId, …}] } */
export async function listAgentMcpOverrides(networkId, poolId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agent-mcp-overrides`,
  );
}

/** Pin an agent's MCP to a configured instanceId. */
export async function setAgentMcpInstanceOverride(networkId, poolId, agentId, mcpId, instanceId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agents/${encodePath(agentId)}/mcp-overrides/${encodePath(mcpId)}`,
    { method: 'PUT', body: JSON.stringify({ instanceId: instanceId || 'default' }) },
  );
}

/** Clear an agent's MCP-instance pin (falls back to the frozen instance). */
export async function clearAgentMcpInstanceOverride(networkId, poolId, agentId, mcpId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agents/${encodePath(agentId)}/mcp-overrides/${encodePath(mcpId)}`,
    { method: 'DELETE' },
  );
}

// ── Room-Wide (pool) MCP INSTANCE pins ──────────────────────────────────────
// The pool-wide analog of the per-agent pin: points an MCP at a configured
// instance for EVERY agent in the room (a per-agent pin still beats it). Lets a
// common tool be shared across the room in one scope; via union hydration the
// builder loads it even on agents that didn't enable that MCP at build time.

/** Pin an MCP room-wide to a configured instanceId (all agents in the pool). */
export async function setPoolMcpInstanceOverride(networkId, poolId, mcpId, instanceId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/mcp-overrides/${encodePath(mcpId)}`,
    { method: 'PUT', body: JSON.stringify({ instanceId: instanceId || 'default' }) },
  );
}

/** Clear the room-wide MCP pin (agents fall back to their own pin, then frozen). */
export async function clearPoolMcpInstanceOverride(networkId, poolId, mcpId) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/mcp-overrides/${encodePath(mcpId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Verify a credential against the upstream service WITHOUT saving it. Returns
 * ``{ ok, message, details }``. Validates the CREDENTIAL (e.g. GitHub PAT is
 * valid) — NOT that the MCP server boots (only a real run proves that). Only
 * available for MCPs with a registered probe (registry ``credentialUi.testSupported``).
 * Room OWNER/BUILDER only.
 */
export async function testMcpCredential(networkId, poolId, mcpId, credentials) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/${encodePath(mcpId)}/test`,
    { method: 'POST', body: JSON.stringify({ credentials: credentials || {} }) },
  );
}

/** Per-agent variant of {@link testMcpCredential} (same probe; path carries the
 *  agent so audit/RBAC match the per-agent save). */
export async function testAgentMcpCredential(networkId, poolId, agentId, mcpId, credentials) {
  return authedFetch(
    `/tools/mcp-credentials/${encodePath(networkId)}/${encodePath(poolId)}/agents/${encodePath(agentId)}/${encodePath(mcpId)}/test`,
    { method: 'POST', body: JSON.stringify({ credentials: credentials || {} }) },
  );
}
